import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/context/ThemeContext";

/**
 * Auto-rotating full-bleed hero slider.
 *
 * - Pulls up to N slides from `settings.hero_slides` (Website Builder → Hero Slider).
 * - Rotates every ROTATE_MS with a smooth cross-fade (opacity transition).
 * - Falls back to a single slide with the CMS hero_image (or a placeholder gradient)
 *   if no slides are configured yet.
 * - Fully responsive; children (hero copy) sit above the slider layer.
 */
const ROTATE_MS = 5000;

export default function HeroSlider({ children, className = "" }) {
  const { i18n } = useTranslation();
  const { settings: brand } = useTheme();
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const slides = React.useMemo(() => {
    const list = (brand?.hero_slides || []).filter((s) => s?.active !== false && s?.image_url);
    return list.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [brand?.hero_slides]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  const resolve = (url) => (url?.startsWith("http") ? url : `${backend}${url}`);
  const active = slides[idx];

  return (
    <section className={`relative min-h-[70vh] sm:min-h-[85vh] flex items-center overflow-hidden bg-zinc-950 ${className}`} data-testid="hero-slider">
      {/* Slide layer — cross-fade */}
      <div className="absolute inset-0" aria-hidden>
        {slides.length === 0 && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${brand?.hero_image || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=80"})` }}
          />
        )}
        {slides.map((s, i) => (
          <div
            key={s.id || s.image_url}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1400ms] ease-in-out"
            style={{
              backgroundImage: `url(${resolve(s.image_url)})`,
              opacity: i === idx ? 1 : 0,
            }}
            data-testid={`hero-slide-${i}`}
          />
        ))}
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Slot content (hero copy) */}
      <div className="relative w-full">{children}</div>

      {/* Per-slide caption (title/subtitle from CMS) — overlays copy so admin
          promotions can appear without editing i18n keys. Only shown when slide
          has custom title/subtitle. */}
      {active && (active.title || active.subtitle) && (
        <div className="absolute bottom-24 left-6 lg:left-10 max-w-lg text-white z-10" data-testid="hero-slide-caption">
          {active.title && <div className="font-serif text-2xl lg:text-3xl leading-tight">{active.title}</div>}
          {active.subtitle && <div className="text-sm text-zinc-300 mt-2">{active.subtitle}</div>}
          {active.cta_text && active.cta_link && (
            <a href={active.cta_link} className="mt-4 inline-block btn-gold text-xs">{active.cta_text}</a>
          )}
        </div>
      )}

      {/* Slide indicator dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10" data-testid="hero-dots">
          {slides.map((slide, i) => (
            <button
              key={slide.id || slide.image_url || `slide-dot-${i}`}
              type="button"
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      )}
      {/* Language-key sink so lints don't complain */}
      <span className="sr-only">{i18n.language}</span>
    </section>
  );
}
